import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import {
  Add,
  CheckCircleOutline,
  DeleteOutline,
  ExpandMore,
  Straighten,
  Thermostat,
} from "@mui/icons-material";
import {
  getDeviceCalibration,
  resetDeviceCalibration,
  setDeviceCalibration,
} from "../../API/deviceCalibration";

// Étalonnage des sondes de température.
//
// Le client compare la valeur de la sonde à celle de son thermomètre de
// référence et enregistre l'écart. La correction est appliquée à la lecture
// côté backend (services/calibration.py) : InfluxDB garde la valeur brute,
// donc l'étalonnage est réversible et se propage à tout l'historique.
//
// Deux principes portent l'ergonomie :
//   1. Ce qui compte n'est pas que la mesure soit RÉCENTE mais que les deux
//      mesures soient SIMULTANÉES. D'où la saisie datée : on compare le relevé
//      du client à l'uplink le plus proche de SON heure, pas au dernier point.
//   2. Une chambre froide oscille au rythme du dégivrage. Un point unique peut
//      donc mesurer un moment du cycle plutôt qu'un défaut de sonde. D'où les
//      relevés multiples et la médiane : si les écarts divergent, ce n'est pas
//      la sonde qu'il faut corriger, c'est son emplacement.
//
// Replié par défaut : un client venu régler ses seuils ne doit pas tomber sur
// des champs d'étalonnage et se demander s'il faut les remplir.

const SOFT_OFFSET = 5; // Au-delà : ressemble à une sonde mal placée — on prévient, sans bloquer.
const MAX_OFFSET = 15; // Plafond de sûreté : au-delà, sonde HS ou faute de frappe.
const MAX_SPREAD = 1; // Écarts dispersés => on prévient (la sonde ne se corrige pas par un simple décalage).
const NEGLIGIBLE_OFFSET = 0.2; // Sous le bruit de mesure.
const MAX_GAP_MINUTES = 30; // Au-delà, les deux mesures ne sont plus simultanées.

const median = (nums) => {
  const sorted = [...nums].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2
    ? sorted[mid]
    : (sorted[mid - 1] + sorted[mid]) / 2;
};

const pad = (n) => String(n).padStart(2, "0");

// Format attendu par <input type="datetime-local"> (heure locale, pas UTC).
const toLocalInput = (date) => {
  const d = new Date(date);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours()
  )}:${pad(d.getMinutes())}`;
};

const formatDateTime = (value) => {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`;
};

// Uplink le plus proche de l'heure saisie, avec l'écart de temps qui permet de
// dire si les deux mesures sont réellement comparables.
const nearestReading = (readings, timeStr) => {
  if (!timeStr || !readings?.length) return null;
  const target = new Date(timeStr).getTime();
  if (Number.isNaN(target)) return null;

  let best = null;
  readings.forEach((r) => {
    const t = new Date(r.time).getTime();
    if (Number.isNaN(t)) return;
    const gap = Math.abs(t - target);
    if (!best || gap < best.gap) best = { raw: r.raw, time: r.time, gap };
  });
  return best;
};

// Cadence réelle déduite des uplinks : médiane des écarts entre points, plutôt
// qu'une moyenne qu'un seul trou de remontée fausserait.
const cadenceMs = (readings) => {
  if (!readings || readings.length < 3) return null;
  const deltas = [];
  for (let i = 1; i < readings.length; i += 1) {
    const delta = new Date(readings[i].time) - new Date(readings[i - 1].time);
    if (delta > 0) deltas.push(delta);
  }
  if (!deltas.length) return null;
  const m = median(deltas);
  return m > 0 ? m : null;
};

function NextReadingHint({ readings }) {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(id);
  }, []);

  const hint = useMemo(() => {
    const cadence = cadenceMs(readings);
    if (!cadence || !readings?.length) return null;

    const last = new Date(readings[readings.length - 1].time).getTime();
    let next = last + cadence;
    // La sonde a pu manquer des remontées : on avance jusqu'à la prochaine
    // échéance réellement à venir plutôt que d'annoncer une heure passée.
    while (next < now) next += cadence;

    const minutes = Math.round((next - now) / 60000);
    const cadenceMin = Math.round(cadence / 60000);
    const cadenceLabel =
      cadenceMin >= 60
        ? `toutes les ${Math.round(cadenceMin / 60)} h`
        : `toutes les ${cadenceMin} min`;

    if (minutes <= 1) {
      return `Cette sonde émet ${cadenceLabel}. Prochaine mesure dans moins d'une minute.`;
    }
    return `Cette sonde émet ${cadenceLabel}. Prochaine mesure dans ${minutes} min.`;
  }, [readings, now]);

  if (!hint) return null;

  return (
    <Typography
      sx={{ fontSize: "0.78rem", color: "var(--text-secondary)", mt: 0.5 }}
    >
      ⏱ {hint}
    </Typography>
  );
}

function CalibrationRow({ device, isMobile, onChanged }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [rows, setRows] = useState([]);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    const resp = await getDeviceCalibration(device.dev_eui);
    setData(resp || null);
    setLoading(false);
  }, [device.dev_eui]);

  useEffect(() => {
    load();
  }, [load]);

  // Mémoïsé : sans ça le tableau serait recréé à chaque rendu et relancerait
  // les calculs d'écarts en boucle.
  const readings = useMemo(() => data?.readings || [], [data]);
  const lastRaw = readings.length ? readings[readings.length - 1].raw : null;
  const offset = data?.offset || 0;
  const isCalibrated = Math.abs(offset) > 0;

  const newRow = useCallback(
    () => ({ key: `${Date.now()}-${Math.random()}`, time: toLocalInput(new Date()), real: "" }),
    []
  );

  const startEditing = () => {
    setRows([newRow()]);
    setError(null);
    setEditing(true);
  };

  // On compare le relevé à l'uplink le plus proche de l'heure saisie. Un écart
  // de temps important est signalé (tooFar) mais n'empêche plus le calcul :
  // sur une sonde en cadence horaire, exiger < 30 min bloquait la saisie. Seule
  // l'absence totale de mesure (near null) rend l'écart incalculable.
  const analysed = useMemo(
    () =>
      rows.map((row) => {
        const near = nearestReading(readings, row.time);
        const real = row.real === "" ? null : Number(row.real);
        const realValid = real !== null && !Number.isNaN(real);
        const gapMinutes = near ? Math.round(near.gap / 60000) : null;
        const tooFar = near ? gapMinutes > MAX_GAP_MINUTES : true;
        return {
          ...row,
          raw: near?.raw ?? null,
          gapMinutes,
          tooFar,
          delta: realValid && near ? real - near.raw : null,
        };
      }),
    [rows, readings]
  );

  const deltas = analysed.map((r) => r.delta).filter((d) => d !== null);
  // Arrondi à 2 décimales, comme l'affichage : à 1 décimale, la correction ne
  // retomberait pas exactement sur la valeur saisie par le client dans la modale.
  const computedOffset = deltas.length
    ? Math.round(median(deltas) * 100) / 100
    : null;
  const spread = deltas.length > 1 ? Math.max(...deltas) - Math.min(...deltas) : 0;

  // On ne bloque plus que l'absurde (sonde HS, faute de frappe). Un écart
  // important est signalé mais reste enregistrable : c'est l'exploitant qui
  // décide et qui en porte la responsabilité (valeur tracée à son nom).
  const blocking = useMemo(() => {
    if (computedOffset === null) return null;
    if (Math.abs(computedOffset) > MAX_OFFSET) {
      return `Écart de ${Math.abs(computedOffset).toFixed(
        2
      )} °C : au-delà de ${MAX_OFFSET} °C, il s'agit d'une sonde hors service ou d'une erreur de saisie, pas d'un étalonnage.`;
    }
    return null;
  }, [computedOffset]);

  const warning = useMemo(() => {
    if (computedOffset === null || blocking) return null;
    if (Math.abs(computedOffset) > SOFT_OFFSET) {
      return `Écart important (${Math.abs(computedOffset).toFixed(
        2
      )} °C) : cela ressemble à une sonde mal placée plutôt qu'à un défaut de justesse. Un décalage fixe ne corrigera bien que dans la plage de température actuelle de la chambre — l'idéal reste de déplacer la sonde. Vous pouvez enregistrer si vous assumez ce réglage.`;
    }
    if (spread > MAX_SPREAD) {
      return `Vos écarts vont de ${Math.min(...deltas).toFixed(2)} à ${Math.max(
        ...deltas
      ).toFixed(
        2
      )} °C : ils sont dispersés, la sonde ne se corrige pas par un simple décalage. Vérifiez son emplacement — l'enregistrement reste possible.`;
    }
    if (Math.abs(computedOffset) < NEGLIGIBLE_OFFSET) {
      return "Cet écart est dans le bruit de mesure : l'étalonnage n'apportera rien.";
    }
    if (deltas.length === 1) {
      return "Un seul relevé : précision limitée. Ajoutez des relevés à d'autres moments pour fiabiliser.";
    }
    return null;
  }, [computedOffset, blocking, spread, deltas]);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    const resp = await setDeviceCalibration(device.dev_eui, computedOffset);
    setSaving(false);
    setConfirmOpen(false);

    if (resp?.status === 200) {
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
      setEditing(false);
      await load();
      onChanged?.();
    } else {
      setError(
        resp?.response?.data?.detail ||
          resp?.message ||
          "Erreur lors de l'enregistrement de l'étalonnage."
      );
    }
  };

  const handleReset = async () => {
    setSaving(true);
    setError(null);
    const resp = await resetDeviceCalibration(device.dev_eui);
    setSaving(false);
    setResetOpen(false);

    if (resp?.status === 200) {
      setEditing(false);
      await load();
      onChanged?.();
    } else {
      setError(
        resp?.response?.data?.detail ||
          resp?.message ||
          "Erreur lors du retrait de l'étalonnage."
      );
    }
  };

  const fieldSx = {
    "& .MuiOutlinedInput-root": {
      fontSize: "0.9rem",
      backgroundColor: "var(--bg-card)",
      "& fieldset": { borderColor: "var(--border-medium)" },
    },
    "& .MuiInputLabel-root": { fontSize: "0.82rem" },
  };

  return (
    <Box sx={{ mb: 2 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.75,
          mb: 1,
          flexWrap: "wrap",
        }}
      >
        <Thermostat
          sx={{ fontSize: 18, color: "var(--kpi-rail-temp, #99DCFF)" }}
        />
        <Typography
          sx={{
            fontSize: "0.95rem",
            fontWeight: 600,
            color: "var(--text-primary)",
          }}
        >
          {device.name || device.dev_eui}
        </Typography>
        <Box sx={{ flexGrow: 1 }} />
        {loading ? (
          <CircularProgress size={14} />
        ) : isCalibrated ? (
          <Chip
            size="small"
            icon={<Straighten sx={{ fontSize: 14 }} />}
            label="étalonné"
            sx={{ height: 22, fontSize: "0.7rem" }}
          />
        ) : lastRaw !== null ? (
          <Typography
            sx={{ fontSize: "0.78rem", color: "var(--text-secondary)" }}
          >
            actuel : {lastRaw.toFixed(2)} °C
          </Typography>
        ) : null}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 1, fontSize: "0.78rem" }}>
          {error}
        </Alert>
      )}

      {!loading && !data && (
        <Typography sx={{ fontSize: "0.78rem", color: "var(--text-secondary)" }}>
          Étalonnage indisponible pour cette sonde.
        </Typography>
      )}

      {!loading && data && !editing && (
        <Box>
          {isCalibrated ? (
            <Typography
              sx={{ fontSize: "0.82rem", color: "var(--text-secondary)", mb: 1 }}
            >
              Correction : {offset > 0 ? "+" : ""}
              {offset.toFixed(2)} °C
              {data.set_at ? ` · réglée le ${formatDateTime(data.set_at)}` : ""}
              {data.set_by_name ? ` par ${data.set_by_name}` : ""}
            </Typography>
          ) : (
            <Typography
              sx={{ fontSize: "0.82rem", color: "var(--text-secondary)", mb: 1 }}
            >
              Aucun étalonnage : la sonde s'affiche telle qu'elle mesure.
            </Typography>
          )}
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button
              size="small"
              variant={isCalibrated ? "outlined" : "contained"}
              onClick={startEditing}
              sx={{ fontSize: "0.75rem" }}
            >
              {saved ? <CheckCircleOutline fontSize="small" /> : isCalibrated ? "Modifier" : "Étalonner"}
            </Button>
            {isCalibrated && (
              <Button
                size="small"
                variant="outlined"
                color="inherit"
                onClick={() => setResetOpen(true)}
                sx={{ fontSize: "0.75rem" }}
              >
                Réinitialiser
              </Button>
            )}
          </Box>
        </Box>
      )}

      {!loading && data && editing && (
        <Box>
          <NextReadingHint readings={readings} />

          {analysed.map((row, idx) => (
            <Grid
              container
              spacing={1}
              alignItems="center"
              key={row.key}
              sx={{ mt: 0.25 }}
            >
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Heure du relevé"
                  type="datetime-local"
                  value={row.time}
                  onChange={(e) =>
                    setRows((prev) =>
                      prev.map((r, i) =>
                        i === idx ? { ...r, time: e.target.value } : r
                      )
                    )
                  }
                  size="small"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  sx={fieldSx}
                />
              </Grid>
              <Grid item xs={5} sm={3}>
                <TextField
                  label="Sonde"
                  value={row.raw !== null ? `${row.raw.toFixed(2)}` : "—"}
                  size="small"
                  fullWidth
                  disabled
                  sx={fieldSx}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <Typography sx={{ fontSize: "0.75rem" }}>°C</Typography>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={5} sm={3}>
                <TextField
                  label="Réelle relevée"
                  type="number"
                  value={row.real}
                  onChange={(e) =>
                    setRows((prev) =>
                      prev.map((r, i) =>
                        i === idx ? { ...r, real: e.target.value } : r
                      )
                    )
                  }
                  size="small"
                  fullWidth
                  sx={fieldSx}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <Typography sx={{ fontSize: "0.75rem" }}>°C</Typography>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={2} sm={2}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <Typography
                    sx={{
                      fontSize: "0.8rem",
                      fontWeight: 600,
                      color: "var(--text-secondary)",
                      minWidth: 42,
                    }}
                  >
                    {row.delta !== null
                      ? `${row.delta > 0 ? "+" : ""}${row.delta.toFixed(2)}`
                      : ""}
                  </Typography>
                  {rows.length > 1 && (
                    <IconButton
                      size="small"
                      onClick={() =>
                        setRows((prev) => prev.filter((_, i) => i !== idx))
                      }
                    >
                      <DeleteOutline sx={{ fontSize: 16 }} />
                    </IconButton>
                  )}
                </Box>
              </Grid>
              {row.tooFar && row.time && (
                <Grid item xs={12}>
                  <Typography
                    sx={{ fontSize: "0.72rem", color: "var(--text-secondary)" }}
                  >
                    Mesure de la sonde éloignée de cette heure
                    {row.gapMinutes !== null
                      ? ` (la plus proche est à ${row.gapMinutes} min)`
                      : ""}{" "}
                    : pour plus de précision, calez l'heure sur une remontée de
                    la sonde. Vous pouvez enregistrer malgré tout.
                  </Typography>
                </Grid>
              )}
            </Grid>
          ))}

          <Button
            size="small"
            startIcon={<Add sx={{ fontSize: 16 }} />}
            onClick={() => setRows((prev) => [...prev, newRow()])}
            sx={{ fontSize: "0.72rem", mt: 1 }}
          >
            Ajouter un relevé
          </Button>

          {blocking && (
            <Alert severity="warning" sx={{ mt: 1, fontSize: "0.78rem" }}>
              {blocking}
            </Alert>
          )}
          {warning && (
            <Alert severity="warning" sx={{ mt: 1, fontSize: "0.78rem" }}>
              {warning}
            </Alert>
          )}
          {computedOffset !== null && !blocking && (
            <Typography
              sx={{
                fontSize: "0.85rem",
                fontWeight: 600,
                color: "var(--text-primary)",
                mt: 1,
              }}
            >
              Correction retenue : {computedOffset > 0 ? "+" : ""}
              {computedOffset.toFixed(2)} °C
            </Typography>
          )}

          <Box sx={{ display: "flex", gap: 1, mt: 1.5 }}>
            <Button
              size="small"
              variant="contained"
              disabled={saving || computedOffset === null || !!blocking}
              onClick={() => setConfirmOpen(true)}
              sx={{ fontSize: "0.75rem" }}
            >
              {saving ? <CircularProgress size={14} /> : "Enregistrer"}
            </Button>
            <Button
              size="small"
              variant="outlined"
              color="inherit"
              onClick={() => setEditing(false)}
              sx={{ fontSize: "0.75rem" }}
            >
              Annuler
            </Button>
          </Box>
        </Box>
      )}

      {/* Le "avant → après" en gros est ce qui attrape la faute de frappe :
          quelqu'un qui a tapé -1,8 au lieu de -18,0 le voit ici. */}
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle sx={{ fontSize: "1rem" }}>
          Confirmer l'étalonnage
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: "0.9rem", fontWeight: 600, mb: 1.5 }}>
            {device.name || device.dev_eui}
          </Typography>
          {lastRaw !== null && computedOffset !== null && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1.5 }}>
              <Box>
                <Typography sx={{ fontSize: "1.4rem", fontWeight: 700 }}>
                  {lastRaw.toFixed(2)} °C
                </Typography>
                <Typography sx={{ fontSize: "0.7rem", color: "var(--text-secondary)" }}>
                  affiché aujourd'hui
                </Typography>
              </Box>
              <Typography sx={{ fontSize: "1.2rem" }}>→</Typography>
              <Box>
                <Typography sx={{ fontSize: "1.4rem", fontWeight: 700 }}>
                  {(lastRaw + computedOffset).toFixed(2)} °C
                </Typography>
                <Typography sx={{ fontSize: "0.7rem", color: "var(--text-secondary)" }}>
                  après étalonnage
                </Typography>
              </Box>
            </Box>
          )}
          <Typography sx={{ fontSize: "0.85rem", mb: 1 }}>
            Correction appliquée : {computedOffset > 0 ? "+" : ""}
            {computedOffset?.toFixed(2)} °C
          </Typography>
          <Typography sx={{ fontSize: "0.78rem", color: "var(--text-secondary)" }}>
            Cet ajustement s'applique aussi à l'historique et aux alertes SMS de
            cette sonde.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)} color="inherit">
            Annuler
          </Button>
          <Button onClick={handleSave} variant="contained" disabled={saving}>
            {saving ? <CircularProgress size={14} /> : "Confirmer"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={resetOpen} onClose={() => setResetOpen(false)}>
        <DialogTitle sx={{ fontSize: "1rem" }}>
          Retirer l'étalonnage
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: "0.9rem", fontWeight: 600, mb: 1.5 }}>
            {device.name || device.dev_eui}
          </Typography>
          {lastRaw !== null && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1.5 }}>
              <Box>
                <Typography sx={{ fontSize: "1.4rem", fontWeight: 700 }}>
                  {(lastRaw + offset).toFixed(2)} °C
                </Typography>
                <Typography sx={{ fontSize: "0.7rem", color: "var(--text-secondary)" }}>
                  affiché aujourd'hui
                </Typography>
              </Box>
              <Typography sx={{ fontSize: "1.2rem" }}>→</Typography>
              <Box>
                <Typography sx={{ fontSize: "1.4rem", fontWeight: 700 }}>
                  {lastRaw.toFixed(2)} °C
                </Typography>
                <Typography sx={{ fontSize: "0.7rem", color: "var(--text-secondary)" }}>
                  valeur brute de la sonde
                </Typography>
              </Box>
            </Box>
          )}
          <Typography sx={{ fontSize: "0.78rem", color: "var(--text-secondary)" }}>
            L'historique et les alertes SMS repartiront de la valeur brute.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResetOpen(false)} color="inherit">
            Annuler
          </Button>
          <Button onClick={handleReset} variant="contained" disabled={saving}>
            {saving ? <CircularProgress size={14} /> : "Confirmer"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

function CalibrationSection({ temperatureDevices, isMobile, onChanged }) {
  // Chargé seulement à l'ouverture : le GET rapatrie 24 h de points bruts par
  // sonde, inutile de le payer sur chaque affichage de la page.
  const [expanded, setExpanded] = useState(false);

  if (!temperatureDevices || temperatureDevices.length === 0) return null;

  const count = temperatureDevices.length;

  return (
    <Grid container spacing={1} sx={{ mt: 0.5 }}>
      <Grid item xs={12}>
        <Accordion
          expanded={expanded}
          onChange={(_, isExpanded) => setExpanded(isExpanded)}
          disableGutters
          sx={{
            borderRadius: "6px !important",
            border: "1px solid var(--border-light)",
            boxShadow: "var(--shadow-sm)",
            backgroundColor: "var(--bg-card)",
            "&:before": { display: "none" },
          }}
        >
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                width: "100%",
                pr: 1,
              }}
            >
              <Straighten sx={{ fontSize: 18, color: "var(--text-secondary)" }} />
              <Typography
                sx={{
                  fontFamily: "var(--font-display)",
                  fontSize: isMobile ? "1rem" : "1.125rem",
                  fontWeight: 600,
                  letterSpacing: "-0.01em",
                  color: "var(--text-primary)",
                }}
              >
                Étalonnage des sondes
              </Typography>
              <Box sx={{ flexGrow: 1 }} />
              <Typography
                sx={{ fontSize: "0.78rem", color: "var(--text-secondary)" }}
              >
                {count} sonde{count > 1 ? "s" : ""}
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ p: isMobile ? 1.5 : 2, pt: 0 }}>
            <Typography
              sx={{
                fontSize: "0.8rem",
                color: "var(--text-secondary)",
                mb: 2,
              }}
            >
              Si votre thermomètre de référence indique une valeur différente,
              ajustez ici. Relevez la température au moment où la sonde émet,
              au même endroit qu'elle, porte fermée. Pour tout renseignement,
              contactez le support EcoLudik.
            </Typography>

            {expanded &&
              temperatureDevices.map((device, idx) => (
                <React.Fragment key={device.dev_eui}>
                  {idx > 0 && <Divider sx={{ my: 1.5 }} />}
                  <CalibrationRow
                    device={device}
                    isMobile={isMobile}
                    onChanged={onChanged}
                  />
                </React.Fragment>
              ))}
          </AccordionDetails>
        </Accordion>
      </Grid>
    </Grid>
  );
}

export default CalibrationSection;
